package org.dronda.api.wishlist

import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.dronda.service.wishlist.core.domain.CreatedEvent
import org.dronda.service.wishlist.core.domain.EmailExclusion
import org.dronda.service.wishlist.core.domain.EventAdminDetails
import org.dronda.service.wishlist.core.domain.EventDetails
import org.dronda.service.wishlist.core.domain.EventUpdate
import org.dronda.service.wishlist.core.domain.Exclusion
import org.dronda.service.wishlist.core.domain.NewEvent
import org.dronda.service.wishlist.core.domain.Participant
import org.dronda.service.wishlist.core.domain.SantaAdminStatus
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection
import org.dronda.service.wishlist.core.domain.SantaSendKind
import org.dronda.service.wishlist.core.domain.SantaSendResult
import org.dronda.service.wishlist.core.domain.SantaViewerStatus
import org.dronda.service.wishlist.core.domain.WishlistEvent
import org.dronda.service.wishlist.core.domain.WishlistSummary
import org.dronda.service.wishlist.core.domain.WishlistUser
import kotlin.uuid.Uuid
import org.dronda.service.wishlist.core.domain.EventMode as DomainEventMode

fun WishlistUser.toApi() = User(
    id = id.toString(),
    username = email,
    name = name,
)

fun DomainEventMode.toApi(): EventMode = when (this) {
    DomainEventMode.STANDARD -> EventMode.STANDARD
    DomainEventMode.SECRET_SANTA -> EventMode.SECRET_SANTA
}

fun EventMode.toDomain(): DomainEventMode = when (this) {
    EventMode.STANDARD -> DomainEventMode.STANDARD
    EventMode.SECRET_SANTA -> DomainEventMode.SECRET_SANTA
}

fun WishlistEvent.toApi() = Event(
    id = id.toString(),
    name = name,
    date = date,
    mode = mode.toApi(),
    budget = budget,
)

fun WishlistSummary.toApi() = WishList(
    id = id.toString(),
    name = name,
    user = owner.toApi(),
)

fun SantaViewerStatus.toApi() = SantaViewerState(
    isDrawn = isDrawn,
    recipientUserId = recipientId?.toString(),
)

fun EventDetails.toApi() = EventWithWishLists(
    event = event.toApi(),
    wishlists = wishlists.map { it.toApi() },
    isAdmin = isAdmin,
    santa = santa?.toApi(),
)

fun CreatedEvent.toApi() = CreateEventResponse(
    eventId = id.toString(),
    unknownEmails = unknownEmails,
)

fun Participant.toApi() = ParticipantStatus(
    user = user.toApi(),
    isAdmin = isAdmin,
    isAssigned = isAssigned,
    lastEmailedAt = lastEmailedAt?.toLocalDateTime(TimeZone.UTC),
)

fun Exclusion.toApi() = ExclusionPair(
    userIdA = userA.toString(),
    userIdB = userB.toString(),
    direction = direction.toApi(),
)

fun SantaExclusionDirection.toApi(): ExclusionDirection = when (this) {
    SantaExclusionDirection.BOTH -> ExclusionDirection.BOTH
    SantaExclusionDirection.A_TO_B -> ExclusionDirection.A_TO_B
    SantaExclusionDirection.B_TO_A -> ExclusionDirection.B_TO_A
}

fun ExclusionDirection.toDomain(): SantaExclusionDirection = when (this) {
    ExclusionDirection.BOTH -> SantaExclusionDirection.BOTH
    ExclusionDirection.A_TO_B -> SantaExclusionDirection.A_TO_B
    ExclusionDirection.B_TO_A -> SantaExclusionDirection.B_TO_A
}

fun SantaSendKind.toApi(): SendKind = when (this) {
    SantaSendKind.INITIAL -> SendKind.INITIAL
    SantaSendKind.SPLICE -> SendKind.SPLICE
    SantaSendKind.RESHUFFLE -> SendKind.RESHUFFLE
}

fun SendKind.toDomain(): SantaSendKind = when (this) {
    SendKind.INITIAL -> SantaSendKind.INITIAL
    SendKind.SPLICE -> SantaSendKind.SPLICE
    SendKind.RESHUFFLE -> SantaSendKind.RESHUFFLE
}

fun SantaAdminStatus.toApi() = SantaAdminState(
    isDrawn = isDrawn,
    sendsUsed = sendsUsed,
    maxSends = maxSends,
    unassignedCount = unassignedCount,
    allowedSendKinds = allowedSendKinds.map { it.toApi() },
    exclusionsChanged = exclusionsChanged,
    exclusions = exclusions.map { it.toApi() },
)

fun EventAdminDetails.toApi() = EventAdminState(
    event = event.toApi(),
    participants = participants.map { it.toApi() },
    maxParticipants = maxParticipants,
    santa = santa?.toApi(),
)

fun SantaSendResult.toApi() = SendAssignmentsResponse(
    emailsSent = emailsSent,
    emailsFailed = emailsFailed,
    sendsRemaining = sendsRemaining,
)

fun EventRequest.toDomain() = NewEvent(
    name = name,
    date = date,
    mode = mode.toDomain(),
    budget = budget,
    participantEmails = participantEmails,
    exclusions = exclusions.map { EmailExclusion(emailA = it.a, emailB = it.b, direction = it.direction.toDomain()) },
)

fun UpdateEventRequest.toDomain() = EventUpdate(
    name = name,
    date = date,
    mode = mode.toDomain(),
    budget = budget,
)

/**
 * Returns `null` when any user id is not a valid uuid.
 */
fun UpdateExclusionsRequest.toDomain(): List<Exclusion>? = exclusions.map { pair ->
    Exclusion(
        userA = Uuid.parseOrNull(pair.userIdA) ?: return null,
        userB = Uuid.parseOrNull(pair.userIdB) ?: return null,
        direction = pair.direction.toDomain(),
    )
}

fun List<Exclusion>.toApi() = UpdateExclusionsRequest(exclusions = map { it.toApi() })

fun User.toDomain() = WishlistUser(
    id = Uuid.parse(id),
    email = username,
    name = name,
)

fun Event.toDomain() = WishlistEvent(
    id = Uuid.parse(id),
    name = name,
    date = date,
    mode = mode.toDomain(),
    budget = budget,
)

fun WishList.toDomain() = WishlistSummary(
    id = Uuid.parse(id),
    name = name,
    owner = user.toDomain(),
)

fun SantaViewerState.toDomain() = SantaViewerStatus(
    isDrawn = isDrawn,
    recipientId = recipientUserId?.let(Uuid::parse),
)

fun EventWithWishLists.toDomain() = EventDetails(
    event = event.toDomain(),
    wishlists = wishlists.map { it.toDomain() },
    isAdmin = isAdmin,
    santa = santa?.toDomain(),
)

fun CreateEventResponse.toDomain() = CreatedEvent(
    id = Uuid.parse(eventId),
    unknownEmails = unknownEmails,
)

fun ParticipantStatus.toDomain() = Participant(
    user = user.toDomain(),
    isAdmin = isAdmin,
    isAssigned = isAssigned,
    lastEmailedAt = lastEmailedAt?.toInstant(TimeZone.UTC),
)

fun ExclusionPair.toDomain() = Exclusion(
    userA = Uuid.parse(userIdA),
    userB = Uuid.parse(userIdB),
    direction = direction.toDomain(),
)

fun SantaAdminState.toDomain() = SantaAdminStatus(
    isDrawn = isDrawn,
    sendsUsed = sendsUsed,
    maxSends = maxSends,
    unassignedCount = unassignedCount,
    allowedSendKinds = allowedSendKinds.map { it.toDomain() },
    exclusionsChanged = exclusionsChanged,
    exclusions = exclusions.map { it.toDomain() },
)

fun EventAdminState.toDomain() = EventAdminDetails(
    event = event.toDomain(),
    participants = participants.map { it.toDomain() },
    maxParticipants = maxParticipants,
    santa = santa?.toDomain(),
)

fun SendAssignmentsResponse.toDomain() = SantaSendResult(
    emailsSent = emailsSent,
    emailsFailed = emailsFailed,
    sendsRemaining = sendsRemaining,
)
